import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import type { HistoryResponse } from "@/components/HistoryDrawer";
import type {
  ActionOrderPayload,
  OrderDecision,
} from "@/components/LinkOrderDialog";
import type {
  LinkChangeDecision,
  LinkChangePending,
  LinkChangeReviewRow,
} from "@/components/LinkChangeReview";
import i18n from "@/lib/i18n/i18n";
import { api } from "@/lib/api/client";
import { API_ORIGIN } from "@/lib/config";
import { getAccessToken } from "@/lib/auth/tokens";
import type { components } from "@/lib/api/schema.gen";
import { unwrap } from "@/lib/api/unwrap";

import { toolKeys } from "./keys";
import type {
  DirectoryTool,
  ToolActionImportReport,
  ToolAdmin,
  ToolCatalog,
  ToolFavorite,
  ToolImportReport,
} from "../types";

function authHeaders(): Record<string, string> {
  const token = getAccessToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  // The history API renders diff labels/badges server-side (gettext); forward
  // the active UI language so the drawer isn't stuck in the default locale.
  if (i18n.language) headers["Accept-Language"] = i18n.language;
  return headers;
}

// --- directory ---------------------------------------------------------------

/**
 * The sectors this directory's sector facet offers: the enterprise's own, read
 * from the server so the SPA applies the same rule as the Django rail
 * (`repertoire/sector_facet.py`). Refetched every time a directory mounts
 * (`staleTime: 0`, overriding the client's 30 s): the sectors can change on
 * the profile form, in the Django app or in another tab, and none of those
 * reaches this cache. A change made while a directory is open shows on its
 * next opening, not before: the client does not refetch on window focus.
 */
export function useToolDirectorySectorsQuery() {
  return useQuery({
    queryKey: toolKeys.directorySectors(),
    staleTime: 0,
    queryFn: () =>
      unwrap(api.GET("/api/v1/tools/directory/sectors/", {})) as Promise<
        components["schemas"]["SectorRef"][]
      >,
  });
}

/**
 * Refine facets applied server-side (parity with the Django directory).
 *
 * Filtered on the server rather than over the fetched list so both apps narrow
 * through one `tools.directory._apply_filters` and can't disagree. The keyword
 * search stays client-side, as it does for financial aids.
 */
export interface DirectoryFilters {
  sectors_served?: string[];
  company_sizes_served?: string[];
  organization_types_served?: string[];
}

export function useToolDirectoryQuery(
  action: string | null,
  filters: DirectoryFilters = {},
) {
  return useQuery({
    // Filters are part of the key so a changed facet refetches (server-side).
    queryKey: [...toolKeys.directory(action), filters],
    // Hold the previous result while the new facet loads. Without this the fresh
    // key has no data, `isPending` flips true, and the view's loading branch
    // unmounts the whole filter rail — collapsing the facet group mid-selection.
    placeholderData: keepPreviousData,
    enabled: Boolean(action),
    // 400/403/404 (missing action, no enterprise) are meaningful states the UI
    // renders — don't retry them.
    retry: false,
    queryFn: async () => {
      const query: Record<string, unknown> = { action };
      if (filters.sectors_served?.length)
        query.sectors_served = filters.sectors_served;
      if (filters.company_sizes_served?.length)
        query.company_sizes_served = filters.company_sizes_served;
      if (filters.organization_types_served?.length)
        query.organization_types_served = filters.organization_types_served;
      const data = await unwrap(
        api.GET("/api/v1/tools/directory/", {
          params: { query } as unknown as { query?: never },
        }),
      );
      return data as unknown as DirectoryTool[];
    },
  });
}

// --- favorites ---------------------------------------------------------------

export function useToolFavoritesQuery(action?: string | null) {
  return useQuery({
    queryKey: toolKeys.favorites(action),
    retry: false,
    queryFn: async () => {
      // Unpaginated (pagination_class = None) → a plain array, not an envelope.
      const data = await unwrap(api.GET("/api/v1/tools/favorites/", {}));
      const results = data as unknown as ToolFavorite[];
      return action ? results.filter((f) => f.action === action) : results;
    },
  });
}

export function useAddToolFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { action: string; toolId: string }) => {
      const body = {
        action: input.action,
        tool: input.toolId,
      } as components["schemas"]["ToolFavorite"];
      return unwrap(api.POST("/api/v1/tools/favorites/", { body }));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: toolKeys.all });
    },
  });
}

export function useRemoveToolFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (favoriteId: string) =>
      unwrap(
        api.DELETE("/api/v1/tools/favorites/{id}/", {
          params: { path: { id: favoriteId } },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: toolKeys.all });
    },
  });
}

// --- CQDD back-office (admin) ------------------------------------------------

/** Action catalogue for the picker (system actions + enjeu/objectif lists).
 * The endpoint is an untyped APIView, so this fetches with the bearer token. */
export function useToolActionCatalog(enabled = true) {
  return useQuery({
    queryKey: toolKeys.adminCatalog(),
    enabled,
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<ToolCatalog> => {
      const res = await fetch(`${API_ORIGIN}/api/v1/tools/admin/action-catalog/`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      return (await res.json()) as ToolCatalog;
    },
  });
}

export function useAdminToolsQuery() {
  return useQuery({
    queryKey: toolKeys.adminList(),
    queryFn: async () => {
      // Unpaginated (pagination_class = None) → a plain array, not an envelope.
      const data = await unwrap(api.GET("/api/v1/tools/admin/tools/", {}));
      return data as unknown as ToolAdmin[];
    },
  });
}

export function useCreateTool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<ToolAdmin>) =>
      unwrap(
        api.POST("/api/v1/tools/admin/tools/", {
          body: body as components["schemas"]["ToolAdmin"],
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: toolKeys.admin() });
    },
  });
}

export function useUpdateTool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; body: Partial<ToolAdmin> }) =>
      unwrap(
        api.PUT("/api/v1/tools/admin/tools/{id}/", {
          params: { path: { id: input.id } },
          body: input.body as components["schemas"]["ToolAdmin"],
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: toolKeys.admin() });
    },
  });
}

export function useDeleteTool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.DELETE("/api/v1/tools/admin/tools/{id}/", {
          params: { path: { id } },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: toolKeys.admin() });
    },
  });
}

/** Bulk tool↔action association import (multipart). One (tool_title, action_id)
 * per row; row order sets display order and a listed tool's link set is replaced. */
export function useImportToolActions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<ToolActionImportReport> => {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(
        `${API_ORIGIN}/api/v1/tools/admin/tool-actions/import/`,
        { method: "POST", headers: authHeaders(), body: form },
      );
      const data = await res.json();
      if (!res.ok) throw data;
      return data as ToolActionImportReport;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: toolKeys.admin() });
    },
  });
}

/** One tool's change history for the drawer (untyped APIView → fetch + token). */
export function useToolHistory(id: string | null) {
  return useQuery({
    // Language is part of the key: the server renders labels per Accept-Language,
    // so a language switch must refetch rather than serve a stale-locale cache.
    queryKey: [...toolKeys.adminHistory(id), i18n.language],
    enabled: Boolean(id),
    queryFn: async (): Promise<HistoryResponse> => {
      const res = await fetch(
        `${API_ORIGIN}/api/v1/tools/admin/tools/${id}/history/`,
        { headers: authHeaders() },
      );
      if (!res.ok) throw new Error("HTTP " + res.status);
      return (await res.json()) as HistoryResponse;
    },
  });
}

/** Bulk .xlsx import (multipart). The ToolImportView APIView is untyped in the
 * schema, so this posts with FormData + bearer token directly. */
export function useImportTools() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<ToolImportReport> => {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API_ORIGIN}/api/v1/tools/admin/tools/import/`, {
        method: "POST",
        headers: authHeaders(),
        body: form,
      });
      const data = await res.json();
      if (!res.ok) throw data;
      return data as ToolImportReport;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: toolKeys.admin() });
    },
  });
}


// --- per-action tool order (four-eyes) --------------------------------------

/** The action's current tool order + any staged proposal. */
export function useActionToolOrder(actionId: string) {
  return useQuery({
    queryKey: toolKeys.adminActionOrder(actionId),
    retry: false,
    queryFn: async () => {
      const data = await unwrap(
        api.GET("/api/v1/tools/admin/actions/{action_id}/order/", {
          params: { path: { action_id: actionId } },
        }),
      );
      return data as unknown as ActionOrderPayload;
    },
  });
}

/** Propose / approve / counter-propose an order — the server enforces four-eyes. */
export function useActionToolOrderDecision(actionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (decision: OrderDecision) => {
      const data = await unwrap(
        api.POST("/api/v1/tools/admin/actions/{action_id}/order/", {
          params: { path: { action_id: actionId } },
          body: decision as components["schemas"]["ActionOrderDecision"],
        }),
      );
      return data as unknown as ActionOrderPayload;
    },
    onSuccess: (data) => {
      qc.setQueryData(toolKeys.adminActionOrder(actionId), data);
      // An approved order changes what enterprises see in the directory.
      void qc.invalidateQueries({ queryKey: toolKeys.all });
    },
  });
}


// --- link-set review (four-eyes) ---------------------------------------------

/** The tools whose link change awaits *your* validation (the list banner). */
export function useToolLinkChangesQuery() {
  return useQuery({
    queryKey: toolKeys.adminLinkChanges(),
    retry: false,
    queryFn: async () => {
      const data = await unwrap(api.GET("/api/v1/tools/admin/link-changes/", {}));
      return data as unknown as LinkChangeReviewRow[];
    },
  });
}

/** Approve several pending link changes at once — your own are skipped. */
export function useApproveToolLinkChanges() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestIds: string[]) =>
      unwrap(
        api.POST("/api/v1/tools/admin/link-changes/", {
          body: {
            request_ids: requestIds,
          } as components["schemas"]["LinkSetBatchDecision"],
        }),
      ),
    onSuccess: () => {
      // Approving writes the live links, so the whole slice is stale: the
      // directory shows different tools on the pistes d'action involved.
      void qc.invalidateQueries({ queryKey: toolKeys.all });
    },
  });
}

/** One resource's open link change, or ``null``. */
export function useToolLinkChange(id: string | null) {
  return useQuery({
    queryKey: toolKeys.adminLinkChange(id ?? "none"),
    enabled: Boolean(id),
    retry: false,
    queryFn: async () => {
      const data = await unwrap(
        api.GET("/api/v1/tools/admin/tools/{id}/link-change/", {
          params: { path: { id: id as string } },
        }),
      );
      return ((data as { pending?: LinkChangePending } | undefined)?.pending ??
        null) as LinkChangePending | null;
    },
  });
}

/** Approve / counter-propose one link change — the server enforces four-eyes. */
export function useToolLinkChangeDecision(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (decision: LinkChangeDecision) => {
      const data = await unwrap(
        api.POST("/api/v1/tools/admin/tools/{id}/link-change/", {
          params: { path: { id } },
          body: decision as components["schemas"]["LinkSetDecision"],
        }),
      );
      return ((data as { pending?: LinkChangePending } | undefined)?.pending ??
        null) as LinkChangePending | null;
    },
    onSuccess: (data) => {
      qc.setQueryData(toolKeys.adminLinkChange(id), data);
      void qc.invalidateQueries({ queryKey: toolKeys.all });
    },
  });
}
